import { inject, Injectable } from '@angular/core';
import { distinctUntilChanged, filter, map, shareReplay, switchMap } from 'rxjs/operators';
import {
    getUserToCommissionByCommissionId, getUserToCommissionFetched,
    getUserToCommissions,
} from '../dave-data-module/State/selectors/user-to-commission.selector';
import { combineLatest, Observable } from 'rxjs';
import { getUser } from '../dave-data-module/State/selectors/users.selectors';
import { EntityRoleEntity, EntityRolePermissionNameEnum } from '../dave-data-module/entities/entity-role.entity';
import { getEntityRole, getEntityRoleFetched } from '../dave-data-module/State/selectors/entity-role.selector';
import { State } from '../dave-data-module/State';
import { Store } from '@ngrx/store';
import { UserToCommissionEntity } from '../dave-data-module/entities/user-to-commission.entity';
import { uniqArray } from '../helper/helper';
import { Permissions } from '@dave/types/dist/proto/global/permissions';
import { isNotNullOrUndefined } from 'codelyzer/util/isNotNullOrUndefined';
import { UserToCommissionResolver } from '../dave-data-module/guards/user-to-commission.resolver';
import { getFetched$ } from '../dave-data-module/helper/helper';
import { EntityRoleResolver } from '../dave-data-module/guards/entity-role.resolver';
import { CommissionEntity } from '../dave-data-module/entities/commission.entity';

@Injectable({
  providedIn: 'root'
})
export class CommissionService {
    store = inject(Store<State>);
    u2cResolver = inject(UserToCommissionResolver);
    entityRoleRes = inject(EntityRoleResolver);

    public userToCommissions$(commissionId: number): Observable<UserToCommissionEntity[]> {
        return getFetched$(this.store, getUserToCommissionFetched, getUserToCommissionByCommissionId({commissionId}), this.u2cResolver);
    }
    public entityRoles$(commissionId: number): Observable<EntityRoleEntity[]> {
        return combineLatest([
            this.userToCommissions$(commissionId),
            this.store.select(getUser).pipe(filter(isNotNullOrUndefined)),
        ]).pipe(
            map(([u2cs, user]) => u2cs?.filter((u) => u.UserId === user.Id)),
            switchMap((u2c) =>
                getFetched$(this.store, getEntityRoleFetched, getEntityRole, this.entityRoleRes).pipe(
                    map((er) => er.filter((e) => u2c?.some((u) => u.RoleId === e.Id))),
                ),
            ),
        );
    }
    public iamAssignee$(commissionId: number): Observable<boolean> {
        return combineLatest([
            this.userToCommissions$(commissionId),
            this.store.select(getUser).pipe(filter(isNotNullOrUndefined)),
        ]).pipe(map(([u2cs, user]) => u2cs?.some((u2c) => u2c.UserId === user.Id && u2c.Assignee)));
    }
    public entityRolePermissions$(commissionId: number): Observable<Permissions[]> {
        return this.entityRoles$(commissionId).pipe(
            map((er) => uniqArray(er.map((e) => e.Permissions.map(e => e.name as unknown as Permissions)).flat())),
        );
    }

    commissionsByPermission$(permission: Permissions): Observable<number[]> {
        return combineLatest([
            getFetched$(this.store, getEntityRoleFetched, getEntityRole, this.entityRoleRes).pipe(map((roles) => roles.filter(r => r.Permissions.some(p => p.name === (permission as unknown as EntityRolePermissionNameEnum))))),
            combineLatest([
                getFetched$(this.store, getUserToCommissionFetched, getUserToCommissions, this.u2cResolver),
                this.store.select(getUser).pipe(filter(isNotNullOrUndefined), map((user) => user.Id), distinctUntilChanged()),
            ]).pipe(map(([u2cs, userId]) => {
                const myU2cs = u2cs.filter((u2c) => u2c.UserId === userId);
                const ret: {[key: number]: number[]} = {};
                myU2cs.forEach((u2c) => {
                    if (!ret[u2c.CommissionId]) {
                        ret[u2c.CommissionId] = [];
                    }
                    if (!ret[u2c.CommissionId].includes(u2c.RoleId)) {
                        ret[u2c.CommissionId].push(u2c.RoleId);
                    }
                });
                return ret;
            }))
        ]).pipe(
            map(([roles, commissionToRoles]) => {
                return Object.keys(commissionToRoles).filter(commissionId => roles.some(role => commissionToRoles[commissionId].includes(role.Id))).map(commissionId => {
                    return parseInt(commissionId);
                } );
            })
        );
    }
}
